import {Component, OnInit } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {LocationStrategy} from '@angular/common';

/**
 * DashboardCopyComponent
 *
 * This component serves as the dashboard for Provider Technicians and Business Admins.
 * It provides a comprehensive overview of business performance, including visit statistics,
 * product placements, and onboarding metrics. The dashboard supports filtering by date ranges
 * and drilling down into specific data points.
 *
 * Key Responsibilities:
 * - Display key performance indicators (KPIs) for provider visits and product placements.
 * - Provide date range filtering (Today, Last Week, Last Month, Last Quarter, Custom).
 * - Manage lists of Provider Managers and Technicians for hierarchical data viewing.
 * - Display product catalogs categorized by type (Healthcare, Feed, Other).
 * - Handle drill-down popups for detailed lists (Dealers, Farmers, Consultants, Products Placed/Returned).
 * - Calculate and display ratios (e.g., Own vs Competitor surveys).
 *
 * User Roles:
 * - Business Admin: Views aggregated data for the entire business and can drill down by manager.
 * - Provider Technician: Views data specific to their assigned tasks and visits.
 *
 * Features:
 * - **Stats Dashboard**: Visual representation of visits, approvals, declines, and financial metrics.
 * - **Date Filtering**: Flexible date selection for historical data analysis.
 * - **Product Catalog**: Categorized view of available products.
 * - **Drill-down Reports**: Detailed lists for onboarding and product transactions.
 * - **Responsive Design**: Adapts layout for mobile and desktop views.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`) and loads business persons (Managers/Technicians).
 * 2. Sets default tab and date range (Today).
 * 3. Fetches dashboard statistics based on selected date range and user role.
 * 4. Fetches product catalog data.
 * 5. User interactions (date change, tab switch, popup open) trigger data refreshes.
 *
 * @component
 * @selector app-dashboard-copy
 * @example
 * <app-dashboard-copy></app-dashboard-copy>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  /**
   * Current active tab index (0 for Overview, 1 for Stats).
   * @type {number}
   * @default 0
   */
  public tabIndex: number = 0;

 
  /**
   * Constructor for DashboardCopyComponent
   *
   * Initializes the component and injects required services.
   *
   * @constructor
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   */
  constructor(
    public route: ActivatedRoute, 
    private previousRouteService: PreviousRouteService, 
    private locationStrategy: LocationStrategy, 
  ) {
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Initializes lists for managers and technicians.
   * 2. Retrieves route data permissions.
   * 3. Tracks navigation history.
   * 4. Fetches business persons to set up the dashboard context.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
  }

  /**
   * Sets the active tab index and triggers data refresh.
   *
   * - Resets date filters.
   * - If tab 0 (Overview), fetches stats directly.
   * - If other tabs, sets date to 'today' which triggers fetch.
   *
   * @method setTabIndex
   * @param {number} index - The index of the tab to activate.
   * @returns {void}
   */
  public setTabIndex(index: number) {
    this.tabIndex = index;
  }

}
